import { CSSProperties, useCallback, useEffect, useRef, useState } from 'react';
import { databaseService, FileDrop } from '../services/database';
import { createPCAgentClient } from '../services/pcAgent';
import { Operator, PCInstance } from '../types/database';

// ---------------------------------------------------------------------------
// FileDropView — admin-gated manual file transfer between PCs and operators.
//
// Files are parked in the private "file-drop" Storage bucket and tracked in
// operations.file_drops (7-day retention, nightly cleanup cron). Sources:
// "Grab from PC" asks that PC's agent (POST /api/filedrop/upload) to push
// local paths into the drop; "Upload from this machine" streams a picked file
// straight to Storage via the file-drop-url broker. Any drop can then be
// downloaded here (signed URL) or sent to any online PC's inbox
// (POST /api/filedrop/fetch → C:\Reality\Inbox).
// ---------------------------------------------------------------------------

interface FileDropViewProps {
  operator: Operator | null;
}

// ---- shared styles (TestLabView / InsightsView visual language) -------------

const card: CSSProperties = {
  background: '#fff',
  border: '1px solid #e4e9f0',
  borderRadius: 12,
  padding: 20,
  marginBottom: 16,
};

const labelStyle: CSSProperties = {
  display: 'block',
  fontSize: 12,
  fontWeight: 600,
  color: '#54626f',
  marginBottom: 4,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
};

const inputStyle: CSSProperties = {
  padding: '6px 8px',
  border: '1px solid #d5dce5',
  borderRadius: 8,
  fontSize: 13,
  width: '100%',
  boxSizing: 'border-box',
};

const buttonStyle: CSSProperties = {
  padding: '6px 14px',
  border: '1px solid #2B6AC0',
  borderRadius: 8,
  background: '#2B6AC0',
  color: '#fff',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
};

const subtleButtonStyle: CSSProperties = {
  ...buttonStyle,
  background: '#fff',
  color: '#2B6AC0',
};

const dangerButtonStyle: CSSProperties = {
  ...buttonStyle,
  border: '1px solid #c0392b',
  background: '#fff',
  color: '#c0392b',
};

const thStyle: CSSProperties = {
  textAlign: 'left',
  fontSize: 12,
  fontWeight: 600,
  color: '#54626f',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  padding: '8px 10px',
  borderBottom: '1px solid #e4e9f0',
};

const tdStyle: CSSProperties = {
  padding: '10px 10px',
  borderBottom: '1px solid #eef2f6',
  fontSize: 13,
  color: '#20303f',
  verticalAlign: 'top',
};

// ---- helpers ----------------------------------------------------------------

function formatBytes(bytes: number | null): string {
  if (bytes === null || bytes === undefined) return '—';
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB'];
  let v = bytes / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(v >= 10 ? 0 : 1)} ${units[i]}`;
}

function timeAgo(iso: string): string {
  const secs = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (secs < 60) return 'just now';
  if (secs < 3600) return `${Math.floor(secs / 60)} min ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)} h ago`;
  return `${Math.floor(secs / 86400)} d ago`;
}

function expiresIn(iso: string): string {
  const secs = (new Date(iso).getTime() - Date.now()) / 1000;
  if (secs <= 0) return 'expired';
  if (secs < 3600) return `${Math.max(1, Math.floor(secs / 60))} min`;
  if (secs < 86400) return `${Math.floor(secs / 3600)} h`;
  return `${Math.floor(secs / 86400)} d`;
}

function agentClientFor(pc: PCInstance) {
  const hosts = [pc.ip_address, pc.tailscale_ip].filter(Boolean) as string[];
  return createPCAgentClient(hosts, 9090, pc.id);
}

const STATUS_COLORS: Record<FileDrop['status'], string> = {
  uploading: '#b47d0e',
  ready: '#2F7D53',
  error: '#c0392b',
};

// ---- component ----------------------------------------------------------------

export default function FileDropView({ operator }: FileDropViewProps) {
  const [drops, setDrops] = useState<FileDrop[]>([]);
  const [pcs, setPcs] = useState<PCInstance[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<{ text: string; error?: boolean } | null>(null);

  // Grab-from-PC form
  const [grabPcId, setGrabPcId] = useState('');
  const [grabPaths, setGrabPaths] = useState('');
  const [grabNote, setGrabNote] = useState('');
  const [grabBusy, setGrabBusy] = useState(false);

  // Upload-from-this-machine form
  const [uploadNote, setUploadNote] = useState('');
  const [uploadBusy, setUploadBusy] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Per-drop send target + busy flag
  const [sendTargets, setSendTargets] = useState<Record<string, string>>({});
  const [sendBusy, setSendBusy] = useState<Record<string, boolean>>({});
  const [deleteBusy, setDeleteBusy] = useState<Record<string, boolean>>({});

  const refresh = useCallback(async () => {
    const rows = await databaseService.listFileDrops();
    setDrops(rows);
    setLoading(false);
  }, []);

  useEffect(() => {
    refresh();
    databaseService.getAllPCInstances().then(setPcs);
  }, [refresh]);

  // Poll while any drop is still uploading so the row flips to ready in place.
  useEffect(() => {
    if (!drops.some((d) => d.status === 'uploading')) return;
    const t = setInterval(refresh, 4000);
    return () => clearInterval(t);
  }, [drops, refresh]);

  const notify = (text: string, error = false) => setMessage({ text, error });

  const handleGrab = async () => {
    const pc = pcs.find((p) => p.id === grabPcId);
    const paths = grabPaths
      .split('\n')
      .map((p) => p.trim())
      .filter(Boolean);
    if (!pc || paths.length === 0) {
      notify('Pick a PC and enter at least one path.', true);
      return;
    }
    setGrabBusy(true);
    try {
      const res = await agentClientFor(pc).fileDropUpload(paths, grabNote || undefined);
      if (res.success && res.data) {
        notify(`${pc.pc_name ?? 'PC'} is uploading "${res.data.file_name}" — it appears below when ready.`);
        setGrabPaths('');
        setGrabNote('');
        refresh();
      } else {
        notify(`Grab failed: ${res.error ?? 'unknown error'}`, true);
      }
    } finally {
      setGrabBusy(false);
    }
  };

  const handleUpload = async () => {
    const file = fileInputRef.current?.files?.[0];
    if (!file) {
      notify('Pick a file first.', true);
      return;
    }
    setUploadBusy(true);
    try {
      const drop = await databaseService.uploadFileDropFromOperator(
        file,
        uploadNote || undefined,
        operator?.name ?? undefined
      );
      if (drop) {
        notify(`Uploaded "${drop.file_name}".`);
        setUploadNote('');
        if (fileInputRef.current) fileInputRef.current.value = '';
        refresh();
      } else {
        notify('Upload failed — see console for details.', true);
      }
    } finally {
      setUploadBusy(false);
    }
  };

  const handleSend = async (drop: FileDrop) => {
    const pc = pcs.find((p) => p.id === sendTargets[drop.id]);
    if (!pc) {
      notify('Pick a target PC first.', true);
      return;
    }
    setSendBusy((b) => ({ ...b, [drop.id]: true }));
    try {
      const res = await agentClientFor(pc).fileDropFetch(drop.id);
      if (res.success && res.data) {
        notify(`Sending "${drop.file_name}" to ${pc.pc_name ?? 'PC'} → ${res.data.dest}`);
      } else {
        notify(`Send failed: ${res.error ?? 'unknown error'}`, true);
      }
    } finally {
      setSendBusy((b) => ({ ...b, [drop.id]: false }));
    }
  };

  const handleDelete = async (drop: FileDrop) => {
    if (!confirm(`Delete "${drop.file_name}" from the drop?`)) return;
    setDeleteBusy((b) => ({ ...b, [drop.id]: true }));
    try {
      const ok = await databaseService.deleteFileDrop(drop);
      if (ok) {
        notify(`Deleted "${drop.file_name}".`);
        refresh();
      } else {
        notify('Delete failed — see console for details.', true);
      }
    } finally {
      setDeleteBusy((b) => ({ ...b, [drop.id]: false }));
    }
  };

  const pcOptions = pcs.map((pc) => {
    const online = databaseService.isPCOnline(pc.last_heartbeat);
    return (
      <option key={pc.id} value={pc.id}>
        {pc.pc_name ?? pc.mac_address} {online ? '' : '(offline)'}
      </option>
    );
  });

  return (
    <div style={{ padding: 20, maxWidth: 1100, margin: '0 auto' }}>
      <h2 style={{ margin: '0 0 4px' }}>File Drop</h2>
      <p style={{ margin: '0 0 16px', color: '#54626f', fontSize: 13 }}>
        Move log files, configs and other one-off files between PCs and this machine. Everything
        here is deleted automatically after 7 days. Files sent to a PC land in{' '}
        <code>C:\Reality\Inbox</code>.
      </p>

      {message && (
        <div
          style={{
            ...card,
            padding: '10px 16px',
            borderColor: message.error ? '#e5b4ad' : '#bcd8c6',
            background: message.error ? '#fdf3f2' : '#f2faf5',
            color: message.error ? '#c0392b' : '#2F7D53',
            fontSize: 13,
          }}
        >
          {message.text}
        </div>
      )}

      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        {/* Grab from a PC */}
        <div style={{ ...card, flex: '1 1 380px' }}>
          <h3 style={{ margin: '0 0 12px', fontSize: 15 }}>Grab files from a PC</h3>
          <label style={labelStyle}>PC</label>
          <select style={inputStyle} value={grabPcId} onChange={(e) => setGrabPcId(e.target.value)}>
            <option value="">Select a PC…</option>
            {pcOptions}
          </select>
          <label style={{ ...labelStyle, marginTop: 12 }}>Paths on that PC (one per line, globs ok)</label>
          <textarea
            style={{ ...inputStyle, minHeight: 64, fontFamily: 'monospace', fontSize: 12 }}
            placeholder={'C:\\Reality\\Logs\\RealityAgent\\*.log\nC:\\Reality\\Deployment\\config.yaml'}
            value={grabPaths}
            onChange={(e) => setGrabPaths(e.target.value)}
          />
          <label style={{ ...labelStyle, marginTop: 12 }}>Note (optional)</label>
          <input
            style={inputStyle}
            placeholder="Why you grabbed this"
            value={grabNote}
            onChange={(e) => setGrabNote(e.target.value)}
          />
          <div style={{ marginTop: 12 }}>
            <button style={buttonStyle} onClick={handleGrab} disabled={grabBusy}>
              {grabBusy ? 'Requesting…' : 'Grab to drop'}
            </button>
          </div>
          <p style={{ margin: '10px 0 0', color: '#8595a5', fontSize: 12 }}>
            One file uploads as-is; multiple files or folders arrive as a single zip.
          </p>
        </div>

        {/* Upload from this machine */}
        <div style={{ ...card, flex: '1 1 320px' }}>
          <h3 style={{ margin: '0 0 12px', fontSize: 15 }}>Upload from this machine</h3>
          <label style={labelStyle}>File</label>
          <input ref={fileInputRef} type="file" style={{ fontSize: 13 }} />
          <label style={{ ...labelStyle, marginTop: 12 }}>Note (optional)</label>
          <input
            style={inputStyle}
            placeholder="What this file is for"
            value={uploadNote}
            onChange={(e) => setUploadNote(e.target.value)}
          />
          <div style={{ marginTop: 12 }}>
            <button style={buttonStyle} onClick={handleUpload} disabled={uploadBusy}>
              {uploadBusy ? 'Uploading…' : 'Upload to drop'}
            </button>
          </div>
        </div>
      </div>

      {/* Drop contents */}
      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <h3 style={{ margin: 0, fontSize: 15 }}>In the drop</h3>
          <button style={subtleButtonStyle} onClick={refresh}>
            Refresh
          </button>
        </div>
        {loading ? (
          <p style={{ color: '#8595a5', fontSize: 13 }}>Loading…</p>
        ) : drops.length === 0 ? (
          <p style={{ color: '#8595a5', fontSize: 13 }}>Nothing in the drop right now.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={thStyle}>File</th>
                  <th style={thStyle}>From</th>
                  <th style={thStyle}>Size</th>
                  <th style={thStyle}>Added</th>
                  <th style={thStyle}>Expires</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {drops.map((drop) => (
                  <tr key={drop.id}>
                    <td style={tdStyle}>
                      <div style={{ fontWeight: 600 }}>{drop.file_name}</div>
                      {drop.note && <div style={{ color: '#8595a5', fontSize: 12 }}>{drop.note}</div>}
                      {drop.status === 'error' && drop.error && (
                        <div style={{ color: '#c0392b', fontSize: 12 }}>{drop.error}</div>
                      )}
                    </td>
                    <td style={tdStyle}>{drop.source_pc_name ?? drop.source}</td>
                    <td style={tdStyle}>{formatBytes(drop.size_bytes)}</td>
                    <td style={tdStyle}>{timeAgo(drop.created_at)}</td>
                    <td style={tdStyle}>{expiresIn(drop.expires_at)}</td>
                    <td style={{ ...tdStyle, color: STATUS_COLORS[drop.status], fontWeight: 600 }}>
                      {drop.status}
                    </td>
                    <td style={tdStyle}>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        {drop.status === 'ready' && drop.signed_url && (
                          <a
                            href={drop.signed_url}
                            target="_blank"
                            rel="noreferrer"
                            style={{ ...subtleButtonStyle, textDecoration: 'none', display: 'inline-block' }}
                          >
                            Download
                          </a>
                        )}
                        {drop.status === 'ready' && (
                          <>
                            <select
                              style={{ ...inputStyle, width: 170 }}
                              value={sendTargets[drop.id] ?? ''}
                              onChange={(e) =>
                                setSendTargets((t) => ({ ...t, [drop.id]: e.target.value }))
                              }
                            >
                              <option value="">Send to PC…</option>
                              {pcOptions}
                            </select>
                            <button
                              style={buttonStyle}
                              onClick={() => handleSend(drop)}
                              disabled={sendBusy[drop.id] || !sendTargets[drop.id]}
                            >
                              {sendBusy[drop.id] ? 'Sending…' : 'Send'}
                            </button>
                          </>
                        )}
                        <button
                          style={dangerButtonStyle}
                          onClick={() => handleDelete(drop)}
                          disabled={deleteBusy[drop.id]}
                        >
                          {deleteBusy[drop.id] ? 'Deleting…' : 'Delete'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
